<template>
	<div class="popup-header">
		<h2 class="popup-headline">{{ t('popup.media.titleDetails') }} {{ media.filename }}</h2>
		<div
			@click="closeDetails"
			class="popup-header__back">
			<i class="fas fa-chevron-left"></i>
			<span>{{ t('popup.media.button.back') }}</span>
		</div>
	</div>
	<div class="media-details">
		<div class="media-details__file">
			<figure class="media-figure">
				<img v-if="media.mime_type.includes('image')" :src="imageURL" alt="">
				<canvas v-else-if="isLottieMedia" ref="lottieCanvas" style="width:100%;height:200px"></canvas>
				<video v-else-if="media.mime_type.includes('video')" controls :src="imageURL"></video>
				<i v-else class="fas fa-file-alt media-figure__icon"></i>
			</figure>
			<div class="button-wrapper media-details__button-wrapper mb--30 mb--42:desktop">
				<div
					@click="copyURL(imageURL)"
					class="button button--popup media-details__button"
				>
					<i class="fas fa-link"></i> <span>{{ t('popup.media.button.copy') }}</span>
				</div>
				<div
					@click="updateMedia"
					:class="updateClassname"
					class="button button--popup bg--dusky-rose media-details__button"
				>
					<i class="fas fa-save"></i> <span>{{ t('popup.media.button.update') }}</span>
				</div>
			</div>
			<div class="popup-label mb--20">{{ t('popup.media.label.templates') }}</div>
			<file-meta
				v-model:target-containers="settings.target_containers"
				v-model:content-types="settings.content_types"
			></file-meta>
		</div>
		<div class="media-details__info">
			<alert-message
				v-if="alert.isShown"
				:alert="alert"
				@close-alert="alertHide"
				class="popup-alert"
			></alert-message>
      <language-control
        :translation-statuses="settings.lang"
        v-model:language-current="languageCurrent"
        @status-changed="translatedStatusChange"
        :mods-classname="'scrollbar-custom--hor mod--tabs'"
      ></language-control>
			<div class="field-row mt--30">
				<field-regular
					:label="t('popup.media.label.title')"
					:placeholder="t('popup.media.placeholder.title')"
					v-model:model-value="settings.lang[languageCurrent.id].headline"
					@on-change="translatedFieldChanged($event, {id: languageCurrent.id, field: 'headline', langObj: settings.lang})"
					@on-keydown="translatedCare"
					:return-null-if-empty="false"
					class="mb--30"
				></field-regular>
			</div>
			<div class="field-row">
				<field-regular
					:label="t('popup.media.label.summary')"
					:placeholder="t('popup.media.placeholder.summary')"
					v-model:model-value="settings.lang[languageCurrent.id].caption"
					@on-change="translatedFieldChanged($event, {id: languageCurrent.id, field: 'caption', langObj: settings.lang})"
					@on-keydown="translatedCare"
					:return-null-if-empty="false"
					class="mb--30"
				></field-regular>
			</div>
			<div class="field-row">
				<field-regular
					:label="t('popup.media.label.keywords')"
					:placeholder="t('popup.media.placeholder.keywords')"
					v-model:model-value="settings.lang[languageCurrent.id].keywords"
					@on-change="translatedFieldChanged($event, {id: languageCurrent.id, field: 'keywords', langObj: settings.lang})"
					@on-keydown="translatedCare"
					:return-null-if-empty="false"
					class="mb--30"
				></field-regular>
			</div>
			<div class="field-row">
				<field-regular
					:label="t('popup.media.label.searchTerms')"
					:placeholder="t('popup.media.placeholder.searchTerms')"
					v-model:model-value="settings.lang[languageCurrent.id].search_terms"
					@on-change="translatedFieldChanged($event, {id: languageCurrent.id, field: 'search_terms', langObj: settings.lang})"
					@on-keydown="translatedCare"
					:return-null-if-empty="false"
					class="mb--30"
				></field-regular>
			</div>
			<div v-if="!isTemplatesClient" class="field-row">
				<field-regular
					:label="t('popup.media.label.categories')"
					:placeholder="t('popup.media.placeholder.categories')"
					v-model:model-value="settings.lang[languageCurrent.id].subjects"
					@on-change="translatedFieldChanged($event, {id: languageCurrent.id, field: 'subjects', langObj: settings.lang})"
					@on-keydown="translatedCare"
					:return-null-if-empty="false"
				></field-regular>
			</div>
			
			<div class="field-row mt--30">
				<field-regular
					:label="t('popup.media.label.author')"
					:placeholder="t('popup.media.placeholder.author')"
					v-model:model-value="settings.credit"
					:return-null-if-empty="false"
					:disabled="!isMainLanguageCurrent"
					class="mb--30"
				></field-regular>
			</div>
			<div class="field-row">
				<field-regular
					:label="t('popup.media.label.contact')"
					:placeholder="t('popup.media.placeholder.contact')"
					v-model:model-value="settings.contacts"
					:return-null-if-empty="false"
					:disabled="!isMainLanguageCurrent"
					class="mb--30"
				></field-regular>
			</div>
			<div class="field-row">
				<field-regular
					:label="t('popup.media.label.copyright')"
					:placeholder="t('popup.media.placeholder.copyright')"
					v-model:model-value="settings.copyright"
					:return-null-if-empty="false"
					:disabled="!isMainLanguageCurrent"
					class="mb--30"
				></field-regular>
			</div>
			<div class="field-row">
				<field-regular
					:label="t('popup.media.label.dateCreation')"
					:placeholder="t('popup.media.placeholder.dateCreation')"
					v-model:model-value="settings.creation_time"
					:return-null-if-empty="false"
					:disabled="!isMainLanguageCurrent"
					class="mb--30"
				></field-regular>
			</div>
			<div class="field-row">
				<field-regular
					:label="t('popup.media.label.authorText')"
					:placeholder="t('popup.media.placeholder.authorText')"
					v-model:model-value="settings.byline"
					:return-null-if-empty="false"
					:disabled="!isMainLanguageCurrent"
					class="mb--30"
				></field-regular>
			</div>
			<template v-if="isTemplatesClient && categories">
				<div class="field-row mb--10">
					<div class="field-item">
						<div class="field-label">{{ t('popup.media.label.categories') }}</div>
						<div class="button-wrapper">
							<div @click="categoryFieldToggle" class="button button--plain button--h-25">
								<i class="fas fa-plus-circle mr--15"></i>
								<span>Add category</span>
							</div>
						</div>
					</div>
				</div>
				<div v-if="isCategoryFieldActive" class="field-row mb--10">
					<field-regular
						:placeholder="t('popup.media.placeholder.categories')"
						v-model:model-value="categoryField"
					></field-regular>
					<div class="field-item">
						<div class="button-wrapper">
							<div
								:class="isCategoryCreateAvailableClassname"
								@click="categoryCreate" class="button bg--greeny-blue button--plain button--h-25 mr--15">
								<i class="fas fa-check"></i>
							</div>
							<div @click="categoryFieldClose" class="button bg--pink button--plain button--h-25">
								<i class="fas fa-times"></i>
							</div>
						</div>
					</div>
				</div>
				<div v-if="isCategoryFieldActive" class="field-row mb--10">
					<field-regular
						:placeholder="t('popup.media.placeholder.categoriesTranslate')"
						v-model:model-value="categoryTranslateField"
					></field-regular>
				</div>
				<div class="field-row mb--30">
					<field-select
						:placeholder="'Select'"
						:options="categories"
						:property="categoryLang"
						:multiselect="true"
						v-model:multiselected="categoriesSelected"
					></field-select>
				</div>
			</template>
			<div class="field-row">
				<field-regular
					:label="t('popup.media.label.voice')"
					:placeholder="t('popup.media.placeholder.voice')"
					v-model:model-value="settings.language"
					:return-null-if-empty="false"
					:disabled="!isMainLanguageCurrent"
					class="mb--30"
				></field-regular>
			</div>
			<div class="field-row">
				<field-regular
					:label="t('popup.media.label.datePublication')"
					:placeholder="t('popup.media.placeholder.datePublication')"
					v-model:model-value="settings.release_time"
					:return-null-if-empty="false"
					:disabled="!isMainLanguageCurrent"
					class="mb--30"
				></field-regular>
			</div>
			<div class="field-row">
				<field-regular
					:label="t('popup.media.label.source')"
					:placeholder="t('popup.media.placeholder.source')"
					v-model:model-value="settings.source"
					:return-null-if-empty="false"
					:disabled="!isMainLanguageCurrent"
					class="mb--30"
				></field-regular>
			</div>
		</div>
	</div>
</template>

<script>
import {watch, ref, computed, reactive, nextTick, onMounted, onBeforeUnmount} from 'vue';
import FieldRegular from '@/components/fields/FieldRegular';
import AlertMessage from '@/components/ui/alert/AlertMessage.vue';
import FileMeta from '@/components/editor-options/settings/popup/content/media/FileMeta';
import {useStore} from 'vuex';
import deepIsEqual from '@/utils/deepIsEqual';
import useAlert from '@/utils/Alert';
import {ALERT_TYPES} from '@/models/ALERT_TYPES';
import {useI18n} from 'vue-i18n';
import useI18nGlobal from '@/utils/i18n';
import FieldSelect from '@/components/fields/FieldSelect';
import ApiService from '@/utils/ApiService';
import LanguageControl from "@/components/ui/tab/LanguageControl.vue";
import useTranslations from "@/utils/language/useTranslations";
import {DotLottie} from '@lottiefiles/dotlottie-web';

export default {
	components: {LanguageControl, FieldSelect, FieldRegular, AlertMessage, FileMeta},
	emits: ['closeDetails', 'mediaUpdate'],
	props: {
		media: {
			type: Object,
			default: null
		}
	},
	setup(props, {emit}) {
		const {t} = useI18n();
		const {getCurrentLang} = useI18nGlobal();
		// alert
		const {alert, alertHide, alertShow, alertCreate} = useAlert();
		const store = useStore();
		const assetsURL = computed(() => store.getters['mediaAssets/getAssetsURL']);
		const assetsStatsURL = computed(() => store.getters["mediaAssets/getAssetsStatsURL"]);
		const assetsParamMap = computed(() => store.getters["mediaAssets/getAssetsParamMap"]);
		const assetsParamTemplates = computed(() => store.getters["mediaAssets/getAssetsParamTemplates"])
		const baseURL = import.meta.env.VITE_ROOT_API;
		const imageURL = computed(() => `${baseURL}${assetsURL.value}${props.media.key}`);
		const lottieURL = computed(() => `${assetsURL.value}${props.media.key}`);
		const isLottieMedia = computed(() => /\.(lot|lottie|lottie\.json)$/i.test(props.media.filename));
		const lottieCanvas = ref(null);
		let lottieInstance = null;
		const mapID = computed(() => store.getters["maps/getCurrentMapId"]);
		
    // language settings
		const language = computed(() => store.getters["maps/languages/getLanguageComputed"]);
    const languageCurrent = ref(language.value);
		const isMainLanguageCurrent = computed(() => language.value.id === languageCurrent.value.id);
		const {getSubvariants, translatedFieldChanged} = useTranslations()
    
    // make a deep clone of props.media to avoid side effect and create the local object to work with
    const settings = reactive(JSON.parse(JSON.stringify(props.media)));
    // const settings = reactive(JSON.parse(JSON.stringify(devSettings.value)));
				
		const isUpdateAvailable = computed(() => !deepIsEqual(props.media, settings));
		const updateClassname = computed(() => isUpdateAvailable.value ? "" : "disabled");
		function updateMedia() {
			// delete settings.check_translated;
			if(!isUpdateAvailable.value) return;
			emit("mediaUpdate", settings);
		}
		
		// translated
		const isLangDifference = computed(() => !deepIsEqual(props.media.lang, settings.lang));
		
		async function copyURL(url) {
			await navigator.clipboard.writeText(url);
			alertCreate({type: ALERT_TYPES.SUCCESS, message: t('popup.media.message.paste')});
			alertShow();
			setTimeout(() => {
				alertHide();
			}, 1000)
		}
		
		function closeDetails() {
			emit("closeDetails");
		}
		
		// MEDIA TEMPLATES CATEGORIES
		const clientName = computed(() => store.getters["user/getClientName"]);
		const isTemplatesClient = computed(() => clientName.value.toLowerCase() === 'templates');
		
		const categoryLang = ref(getCurrentLang());
		const categories = ref(null);
		
		const categoryField = ref('');
		const categoryTranslateField = ref('');
		const categoryFieldRef = ref(null);
		const isCategoryFieldActive = ref(false);
		
		function categoryFieldToggle() {
			isCategoryFieldActive.value = !isCategoryFieldActive.value;
			categoryField.value = '';
			categoryTranslateField.value = '';
		}
		
		function categoryFieldClose() {
			isCategoryFieldActive.value = false;
			categoryField.value = '';
			categoryTranslateField.value = '';
		}
		
		// The asset lists its categories by ID; the select works with the
		// category objects, whose names it shows.
		const categoriesSelected = computed({
			get: () => (categories.value || []).filter(category => settings.categories.includes(category.id)),
			set: selected => {
				settings.categories = selected.map(category => category.id);
			}
		});

		const isCategoryCreateAvailable = computed(() => categoryField.value.trim().length && categoryTranslateField.value.trim().length);
		const isCategoryCreateAvailableClassname = computed(() => categoryTranslateField.value ? "" : "disabled");
		async function categoryCreate() {
			if(!isCategoryCreateAvailable.value) return;
			try{
				const {data} = await ApiService.post(`${assetsURL.value}categories`, {
					en: categoryField.value.trim(),
					de: categoryTranslateField.value.trim()
				});
				categories.value.push(data);
				settings.categories.push(data.id);
				categoryFieldClose();
			} catch(err){
				const message = err.response?.data?.detail ? err.response.data.detail : t('popup.media.message.categoryCreateError');
				alertCreate({type: ALERT_TYPES.ERROR, message: message});
				alertShow();
			}
		}

    // translation status
    function translatedStatusChange({checked, id}){
      settings.lang[id].translated = checked;
    }
    
    // add function which on every field changing loop through all lang[id] values and if some values aren't empty or null, then set check_translated to true
		function translationStatusCheck(value){
      if(value && !settings.check_translated){
				settings.check_translated = true;
        return;
			}
      if(!value){
        // if any of fields has a value set check_translated to true
        for(const langValues of Object.values(settings.lang)){
          for(const fieldValue of Object.values(langValues)){
            if(fieldValue){
              settings.check_translated = true;
              return;
            }
          }
				}
        // otherwise, set check_translated to false
        settings.check_translated = false;
      }
      
		}
		
		// translated status watch
		function translatedCare(){
			// unchecked translated status of additional languages
			// if them main language fields have been changed
			if(!isLangDifference.value) return;
			const id = languageCurrent.value.id;
			if(id === language.value.id){
				for(const lang of Object.keys(settings.lang)){
					if(lang !== id && settings.lang[lang].translated){
						settings.lang[lang].translated = false;
						alertCreate({type: ALERT_TYPES.SUCCESS, message: t('message.translationsCare')});
						alertShow();
					}
				}
			}
		}
		watch(isUpdateAvailable, (value, oldValue) => {
			if(!value && !isLangDifference.value){
				alertHide();
			}
		})
		
		onMounted(async () => {
			if(isLottieMedia.value && lottieCanvas.value){
				try {
					const response = await ApiService.get(lottieURL.value, {
						responseType: 'blob',
						headers: {'Accept': 'application/octet-stream'}
					});
					const blobUrl = URL.createObjectURL(response.data);
					lottieInstance = new DotLottie({
						canvas: lottieCanvas.value,
						src: blobUrl,
						autoplay: true,
						loop: true
					});
				} catch(e) {
					console.error('Failed to load lottie preview:', e);
				}
			}
		});
		onBeforeUnmount(() => {
			if(lottieInstance){
				lottieInstance.destroy();
				lottieInstance = null;
			}
		});

		// PROCESS
		if(isTemplatesClient.value) fetchCategories();
		
		// FETCH DATA
		async function fetchCategories(){
			const requestURL = `${assetsURL.value}${assetsStatsURL.value}?${assetsParamMap.value}${mapID.value}&${assetsParamTemplates.value}`;
			try{
				({data: {categories: categories.value}} = await ApiService.get(requestURL))
			} catch(err){
				const message = err.response.data?.detail ? err.response.data.detail : t('popup.media.message.errorLoading')
				// alertCreate({type: ALERT_TYPES.SUCCESS, message: message});
			}
		}
		
		return {
			t,
			alert, alertHide,
			isLangDifference,
			translatedCare,
			imageURL,
			isLottieMedia,
			lottieCanvas,
			isUpdateAvailable,
			updateMedia,
			updateClassname,
			copyURL,
			settings,
			closeDetails,
			isTemplatesClient,
			categoryLang,
			categoryField,
			categoryTranslateField,
			categoryFieldRef,
			isCategoryFieldActive,
			isCategoryCreateAvailableClassname,
			categories,
			categoriesSelected,
			categoryFieldToggle,
			categoryFieldClose,
			categoryCreate,
			languageCurrent,
      translatedStatusChange,
			translatedFieldChanged,
			isMainLanguageCurrent,
		}
	}
}

</script>

<style lang="scss">
@use "@/scss/sections/editor/media/media-details";
@use "@/scss/sections/editor/media/media-figure";
</style>
